import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import VisuallyHidden from 'components/consumer/VisuallyHidden';
import useTrapFocus from 'hooks/useTrapFocus';
import IconArrowDownSimple from 'components/icons/iconArrowDownSimple';
import IconArrowRight from 'components/icons/iconArrowRight';

const shopByPages = [
  {
    label: 'Inspiration',
    path: '/customer-photos',
  },
  {
    label: 'Material',
    path: '/swatches',
  },
  {
    label: 'Collection',
    path: '/collections',
  },
  {
    label: 'Style',
    path: '/styles',
  },
];

const ShopByTitleNavigation = ({ children, page }) => {
  const wrapperRef = useRef(null);
  const wrapperElement = wrapperRef.current;
  const [isNavOpen, setIsNavOpen] = useState(false);
  const otherPages = shopByPages.filter(({ label }) => label !== page);
  useTrapFocus(isNavOpen, wrapperRef);

  useEffect(() => {
    if (wrapperElement) {
      if (isNavOpen) {
        disableBodyScroll(wrapperElement);
      } else {
        enableBodyScroll(wrapperElement);
      }
    }

    return () => {
      clearAllBodyScrollLocks();
    };
  }, [wrapperElement, isNavOpen]);

  return (
    <>
      <div
        className={classNames(
          'bg-[rgba(47,47,46,.75)] bottom-0 left-0 fixed right-0 top-0 z-[2]',
          {
            hidden: !isNavOpen,
            block: isNavOpen,
          }
        )}
        onClick={() => {
          setIsNavOpen(false);
        }}
        aria-hidden
      />

      <div className="bg-white relative z-[3]" ref={wrapperRef}>
        <div
          className={classNames(
            'items-center bg-white [border-bottom:1px_solid_#f1f1f1] flex flex-wrap px-5 py-2 md:flex-row md:px-[3.333vw] md:py-4',
            {
              'md:[border-bottom:0]': isNavOpen,
            }
          )}
        >
          <button
            className="border-2 border-solid border-transparent rounded-[3px] px-4 py-1.5 relative left-[-18px] bg-transparent md:mr-16"
            type="button"
            onClick={() => {
              setIsNavOpen(!isNavOpen);
            }}
            aria-expanded={isNavOpen ? 'true' : 'false'}
          >
            {page === 'Inspiration' ? (
              <VisuallyHidden>
                <h1>Customer photos</h1>
              </VisuallyHidden>
            ) : null}

            <h2 className="text-xl font-bold text-gray cursor-pointer no-underline mb-0">
              <span className="text-xl inline-block mb-0 font-bold relative top-[-5px]">
                <span
                  className={classNames({
                    'text-gray-light2': isNavOpen,
                  })}
                >
                  {`Shop by `}
                </span>
                {page}
              </span>

              <span
                className={classNames(
                  'rounded-full inline-block h-7 ml-3 relative w-7 [&_svg]:h-[13px] [&_svg]:left-2 [&_svg]:absolute [&_svg]:w-3',
                  {
                    'bg-gray-light8 [&_svg]:top-2 [&_svg_path]:fill-gray': !isNavOpen,
                    'bg-brand [&_svg]:top-[7px] [&_svg]:rotate-180 [&_svg_path]:fill-white': isNavOpen,
                  }
                )}
              >
                <IconArrowDownSimple />
              </span>
            </h2>
          </button>
          {children}
        </div>

        <div className="relative">
          <ul
            className={classNames(
              'm-0 list-none bg-white [border-bottom:1px_solid_#f1f1f1] left-0 absolute right-0 top-0',
              {
                hidden: !isNavOpen,
                block: isNavOpen,
              }
            )}
          >
            {otherPages.map(({ label, path }) => (
              <li key={label}>
                <Link
                  to={path}
                  className="text-xl font-bold text-gray cursor-pointer no-underline block px-5 py-4 md:px-[3.333vw] md:py-6 hover:md:bg-gray-light8"
                >
                  <span className="border-2 border-solid border-transparent rounded-[3px] px-4 relative left-[-18px] py-[11px]">
                    <span
                      className={classNames({
                        'text-gray-light2': isNavOpen,
                      })}
                    >
                      {`Shop by `}
                    </span>
                    {label}
                    <IconArrowRight className="hidden h-4 ml-[9px] w-6 hover:md:inline-block [&_path]:fill-[#4a4a4a]" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
};

ShopByTitleNavigation.propTypes = {
  children: PropTypes.node,
  page: PropTypes.string,
};

export default ShopByTitleNavigation;
